import React from 'react';

import { EntityType } from '@frontend/common';
import { ObjectBadge } from '@frontend/components/badge';
import { Event } from '@frontend/events/types';

import AsyncComponent from '../async-component';
import { ObjectBadgeMapper } from './object-badge-mapper';

const EVENT_REFERENCE_KEYS = [
    'actor',
    'location',
    'user_id',
    'spot_id',
    'module_id',
    'slot_id',
    'transaction_id',
    'colli_id',
    'package_id',
    'product_id',
    'equipment_id',
    'device_id',
    'iot_id',
    'locker_id',
    'problem_id',
    'user_interface_id',
    'workflow_id',
    'identification_id'
] as const satisfies readonly (keyof Event)[];

type EventReferenceKey = (typeof EVENT_REFERENCE_KEYS)[number];

const DIRECT_ENTITY_TYPES: Partial<Record<EventReferenceKey, EntityType>> = {
    actor: EntityType.USER,
    location: EntityType.SPOT,
    colli_id: EntityType.COLLI,
    package_id: EntityType.PACKAGE,
    locker_id: EntityType.LOCKER,
    identification_id: EntityType.IDENTIFICATION
};

export interface EventReferenceBadgesProps {
    event: Event;
    className?: string;
    queryParamNavigation?: boolean;
    excludeKeys?: EventReferenceKey[];
}

export const EventReferenceBadges = ({ event, className, queryParamNavigation, excludeKeys }: EventReferenceBadgesProps) => {
    const badges = EVENT_REFERENCE_KEYS.flatMap((key) => {
        if (excludeKeys?.includes(key)) {
            return [];
        }

        const id = event[key];
        if (id == null) {
            return [];
        }

        const directEntityType = DIRECT_ENTITY_TYPES[key];
        if (directEntityType) {
            return [
                <AsyncComponent key={`${key}-${id}`}>
                    <ObjectBadge
                        id={id}
                        entityType={directEntityType}
                        queryParamNavigation={queryParamNavigation}
                        parentIds={{
                            ...(key === 'colli_id' && event.transaction_id && { transaction_id: event.transaction_id })
                        }}
                    />
                </AsyncComponent>
            ];
        }

        if (key === 'slot_id') {
            return [
                <AsyncComponent key={`${key}-${id}`}>
                    <ObjectBadge
                        id={id}
                        entityType={EntityType.SLOT}
                        queryParamNavigation={queryParamNavigation}
                        parentIds={{
                            ...(event.spot_id && { spot_id: event.spot_id }),
                            ...(event.module_id && { module_id: event.module_id })
                        }}
                    />
                </AsyncComponent>
            ];
        }

        return [
            <ObjectBadgeMapper
                key={`${key}-${id}`}
                id={id}
                type={key}
                queryParamNavigation={queryParamNavigation}
            />
        ];
    });

    if (badges.length === 0) {
        return null;
    }

    return <div className={`flex w-full flex-wrap items-center gap-2 ${className ?? ''}`}>{badges}</div>;
};
